import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { HttpErrorResponse } from "@angular/common/http";
import { catchError, debounceTime, filter, map, merge, of, switchMap, withLatestFrom } from "rxjs";
import Papa from "papaparse";
import { NavbarService } from "../../../../../layouts/tenant-interactive-layout/navbar.service";
import { PollApiService } from "../../../../services/poll.api.service";
import { SnackbarService } from "../../../../../components/snackbar/snackbar.service";
import { PollId, isPollId, stripPrefix } from "../../../../../shared/models/entity.model";
import { PollResultsTableComponent } from "../../../components/poll-results-table/poll-results-table.component";
import { ButtonComponent, InputComponent, LabelPosition, ToggleComponent } from "@sxm-content-creators/tools-ds";

@Component({
    selector: "app-poll-dashboard",
    templateUrl: "./poll-dashboard.component.html",
    styleUrl: "./poll-dashboard.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        DecimalPipe,
        PollResultsTableComponent,
        ButtonComponent,
        InputComponent,
        ToggleComponent,
        ReactiveFormsModule
    ]
})
export class PollDashboardComponent implements OnInit {
    private readonly pollApi = inject(PollApiService);
    private readonly navbarService = inject(NavbarService);
    private readonly snackbar = inject(SnackbarService);

    private readonly typedInteractiveId$ = toObservable(this.navbarService.typedInteractiveId).pipe(
        filter((id): id is PollId => isPollId(id))
    );
    private readonly reload$ = merge(
        this.typedInteractiveId$,
        this.navbarService.refresh$.pipe(
            withLatestFrom(this.typedInteractiveId$),
            map(([, id]) => id)
        )
    );

    readonly searchControl = new FormControl("");
    readonly searchQuery = signal("");
    readonly writeInOnly = signal(false);

    ngOnInit(): void {
        this.searchControl.valueChanges.pipe(debounceTime(200)).subscribe(value => {
            this.searchQuery.set(value ?? "");
        });
    }

    readonly poll = toSignal(
        this.typedInteractiveId$.pipe(
            switchMap(id =>
                this.pollApi.get(id).pipe(
                    catchError((error: HttpErrorResponse) => {
                        this.snackbar.show(error.error?.detail ?? "We couldn't load the poll.", "error");
                        return of(null);
                    })
                )
            )
        ),
        { initialValue: null }
    );

    readonly results = toSignal(
        this.reload$.pipe(
            switchMap(id =>
                this.pollApi.getResults(stripPrefix(id)).pipe(
                    catchError((error: HttpErrorResponse) => {
                        this.snackbar.show(error.error?.detail ?? "We couldn't load the poll results.", "error");
                        return of(null);
                    })
                )
            )
        ),
        { initialValue: null }
    );

    downloadCSV(): void {
        const poll = this.poll();
        const results = this.results();
        if (!poll || !results) return;

        const rows = results.results.map(item => ({
            Selection: item.label,
            Votes: item.count,
            ...(poll.isMultiSelect ? { "Write-In": item.isWriteIn ? "Yes" : "No" } : {})
        }));

        // Leading BOM tells Excel to read the file as UTF-8
        const blob = new Blob(["\uFEFF" + Papa.unparse(rows)], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${poll.name}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    protected readonly LabelPosition = LabelPosition;
}
